import { LoadingStates } from '@pb/utils'
import { curry, prop, compose, flatten, map, not, isEmpty, filter, head } from 'ramda'

import appSelectors from 'App/duck/selectors'

const getAppState = (state): any => state.app
const getUserMenuState = (state): any => state.account.userMenu
const getUserMenus = (state): any => getUserMenuState(state).userMenus
const getNewUserMenus = (state): any => getUserMenuState(state).newUserMenus
const getSettings = (state): any => getAppState(state).settings

// ----------------------------------------
// REVIEW ITEMS
// ----------------------------------------
const getReviewItems = (state): any => getUserMenuState(state).reviewItems
const getReviewItemsNumber = (state): any => getReviewItems(state).number

// ----------------------------------------
// SPECIFIC VALUES ON MENU ITEMS
// ----------------------------------------

const getPropFromMenuItem = curry((state, key, propToGet) => {
  const isKey = (menuItem): any => menuItem.key === key
  const notEmpty = compose(not, isEmpty)
  const KeyFromMenuItems = compose(filter(isKey), prop('menuItems'))

  return compose(
    head,
    map(prop(propToGet)),
    flatten,
    filter(notEmpty),
    map(KeyFromMenuItems),
  )(getUserMenus(state))
})

const getHasBadge = (state, key): any => getPropFromMenuItem(state, key, 'shouldShowBadge')

const getIsVisible = (state, key): any => getPropFromMenuItem(state, key, 'isVisible')

const getIsDisabled = (state, key): any => getPropFromMenuItem(state, key, 'isDisabled')

// ----------------------------------------
// LOADERS
// ----------------------------------------
const getIsReviewItemsLoading = (state): any => getReviewItems(state).isLoading
const getIsReviewItemsLoadingFailed = (state): any => getReviewItems(state).isLoadingFailed
const getIsReviewItemsRequested = (state): any => getReviewItems(state).isRequested

const getShouldShowReviewItems = (state): any => appSelectors.getIsRateAndReviewEnabled(state)

const getShouldFetchReviewItemsNumber = (state): any =>
  appSelectors.getHasSettingsLoaded(state) &&
  getShouldShowReviewItems(state) && // Should we even show it in the menu?
  !getIsReviewItemsLoading(state) && // ...and it is not loading
  !getIsReviewItemsLoadingFailed(state) && // ...and it have never failed to load
  !getIsReviewItemsRequested(state) // ...and we haven't requested it before

// ----------------------------------------
// DISABLED/ENABLED MENU ITEMS
// ----------------------------------------
const getShouldDisableReviewItems = (state): any =>
  !getIsReviewItemsRequested(state) ||
  getIsReviewItemsLoadingFailed(state) ||
  getReviewItemsNumber(state) === 0

// ----------------------------------------
// VISIBLE MENU ITEMS
// ----------------------------------------
const getShouldShowPayLaterActivationMenuItem = (state): any =>
  appSelectors.getIsPayLaterPostPurchaseEnabled(state) &&
  appSelectors.getIsPayLaterActivationAllowed(state) &&
  !appSelectors.getIsPayLaterActivatedForUser(state)

const getShouldShowPointsHistoryPage = (state): any =>
  appSelectors.getIsPointsHistoryEnabled(state) && appSelectors.getUserIsFullMember(state)
const getShouldShowInviteFriendPage = (state): any => appSelectors.getIsInviteFriendEnabled(state)

const getShouldShowClubInfoPage = (state): any =>
  !!appSelectors.getIsClubMarket(state) && appSelectors.getIsUserClubMember(state)

const getShouldShowPayments = (state): boolean => getSettings(state)?.paymentDecouplingEnabled

const getIsUserMenuLoading = (state): boolean =>
  getUserMenuState(state).loadingState === LoadingStates.Loading
const getIsUserMenuLoadedSuccess = (state): boolean =>
  getUserMenuState(state).loadingState === LoadingStates.LoadedSuccess
const getIsUserMenuLoadedFail = (state): boolean =>
  getUserMenuState(state).loadingState === LoadingStates.LoadedFail

export default {
  getUserMenus,
  getNewUserMenus,
  getHasBadge,
  getIsVisible,
  getIsDisabled,
  getShouldFetchReviewItemsNumber,
  getShouldShowReviewItems,
  getShouldShowPointsHistoryPage,
  getShouldShowInviteFriendPage,
  getShouldShowClubInfoPage,
  getShouldDisableReviewItems,
  getShouldShowPayLaterActivationMenuItem,
  getShouldShowPayments,
  getIsUserMenuLoading,
  getIsUserMenuLoadedSuccess,
  getIsUserMenuLoadedFail,
  getIsReviewItemsRequested,
}
